import {
  EnumBuilder,
  type NonEmptyArray,
  type ZodSimplify,
} from '@mntn-dev/utility-types'
import { z } from 'zod'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  BillingMethodSchema,
  NoneableBillingMethodSchema,
} from './billing/billing-method.ts'
import { BillingScheduleSchema } from './billing/billing-schedule.ts'
import { CurrencySchema } from './billing/currency.ts'
import { InvoiceTermsSchema } from './billing/invoice-terms.ts'
import {
  PackagePayoutSchema,
  PackageTranslationsSchema,
} from './package.models.ts'
import {
  CompanyNameSchema,
  CostMarginPercentSchema,
  DataOriginSchema,
  DateSchema,
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  NonEmptyStringSchema,
  TimestampSchema,
} from './property.models.ts'
import { ReviewRoundCountSchema } from './review.models.ts'
import { TagFilterSchema } from './tag.models.ts'
import {
  FileIdSchema,
  PackageIdSchema,
  ProjectIdSchema,
  ProjectUrnSchema,
  TeamIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * ProjectStatus
 */
export const [ProjectStatuses, ProjectStatusSchema, ProjectStatusEnum] =
  EnumBuilder(
    'draft',
    'archived',
    'pricing',
    'priced',
    'submitted',
    'bidding_open',
    'processing_bids',
    'bidding_closed',
    'offered',
    'accepted',
    'matched',
    'awarded',
    'pre_production',
    'pre_production_complete',
    'production',
    'post_production',
    'post_production_complete',
    'processing_final_files',
    'complete'
  )
export type ProjectStatus = ZodSimplify<typeof ProjectStatusSchema>

export const ActiveProjectStatuses = ProjectStatuses.filter(
  (status) => !['archived', 'complete'].includes(status)
)

export const InProgressProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
]

export const InProductionProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
]

export const BiddingProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.bidding_open,
  ProjectStatusEnum.processing_bids,
  ProjectStatusEnum.bidding_closed,
]

export const AgencyHighlightedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ...BiddingProjectStatuses,
]

export const BrandHighlightedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ...AgencyHighlightedProjectStatuses,
  ProjectStatusEnum.awarded,
]

export const CompletedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.complete,
]

export const AwardedProjectStatusSchema = ProjectStatusSchema.extract([
  'matched',
  'awarded',
  'pre_production',
  'pre_production_complete',
  'production',
  'post_production',
  'post_production_complete',
  'processing_final_files',
  'complete',
])

export const ProjectSubmittedStatusSchema = ProjectStatusSchema.extract([
  'submitted',
  'pricing',
])
export const ProjectSubmittedStatuses = ProjectSubmittedStatusSchema.options
export type ProjectSubmittedStatus = ZodSimplify<
  typeof ProjectSubmittedStatusSchema
>

export const PostProductionCompleteProjectStatuses: NonEmptyArray<ProjectStatus> =
  ProjectStatusSchema.extract([
    'post_production_complete',
    'processing_final_files',
    'complete',
  ]).options

const ProjectInheritedPackagePropertiesSchema = z.object({
  name: NameSchema,
  description: DescriptionSchema,
  translations: PackageTranslationsSchema.optional(),
  cost: PackagePayoutSchema,
  costPlusMargin: DollarsSchema(),
})

/**
 * ProjectDomainSelectModel
 */
export const ProjectDomainSelectModelSchema = z.object({
  projectId: ProjectIdSchema,
  projectUrn: ProjectUrnSchema,
  origin: DataOriginSchema.optional(),
  name: NameSchema,
  description: NonEmptyStringSchema(6144).optional(),
  dueDate: DateSchema.optional(),
  ownerId: UserIdSchema,
  brandTeamId: TeamIdSchema,
  acceptorId: UserIdSchema.optional(),
  agencyTeamId: TeamIdSchema.optional(),
  status: ProjectStatusSchema,
  thumbnailFileId: FileIdSchema.optional(),
  packageId: PackageIdSchema,
  inherited: z.object({ package: ProjectInheritedPackagePropertiesSchema }),
  costMarginPercent: CostMarginPercentSchema,
  preProductionReviewRounds: ReviewRoundCountSchema.optional(),
  postProductionReviewRounds: ReviewRoundCountSchema.optional(),
  biddingCloseDate: DateSchema.optional(),
  billingSchedule: BillingScheduleSchema,
  invoiceTerms: InvoiceTermsSchema,
  billingMethods: z.array(BillingMethodSchema),
  chosenBillingMethod: NoneableBillingMethodSchema,
  currency: CurrencySchema,
  paymentCompletedAt: DateSchema.optional(),
  bidCap: DollarsSchema().optional(),
  bidRecommendation: DollarsSchema().optional(),
})

export type ProjectDomainSelectModel = ZodSimplify<
  typeof ProjectDomainSelectModelSchema
>

export function isProjectWithStatus<
  Project extends ProjectDomainSelectModel,
  Status extends ProjectStatus,
>(project: Project, status: Status): project is Project & { status: Status } {
  return project.status === status
}

/**
 * ProjectViewDomainSelectModelSchema
 */
export const ProjectViewDomainSelectModelSchema =
  ProjectDomainSelectModelSchema.extend({
    lastTouchedTimestamp: TimestampSchema,
    brandCompanyName: CompanyNameSchema,
    cost: DollarsSchema(),
    costPlusMargin: DollarsSchema(),
  })

export type ProjectViewDomainSelectModel = ZodSimplify<
  typeof ProjectViewDomainSelectModelSchema
>

/**
 * ProjectDomainInsertModel
 */
export const ProjectDomainInsertModelSchema =
  ProjectDomainSelectModelSchema.omit({
    projectUrn: true,
    thumbnailFileId: true,
    origin: true,
    bidCap: true,
    bidRecommendation: true,
  })
    .partial({
      projectId: true,
    })
    .extend({
      costMarginPercent: CostMarginPercentSchema,
      inherited: ProjectDomainSelectModelSchema.shape.inherited.extend({
        package: ProjectInheritedPackagePropertiesSchema.omit({
          costPlusMargin: true,
        }),
      }),
    })

export type ProjectDomainInsertModel = ZodSimplify<
  typeof ProjectDomainInsertModelSchema
>

/**
 * ProjectDomainUpdateModel
 */
export const ProjectDomainUpdateModelSchema =
  ProjectDomainSelectModelSchema.omit({
    projectId: true,
    projectUrn: true,
    origin: true,
    packageId: true,
    inherited: true,
    costMarginPercent: true,
  })
    .extend({
      dueDate: ProjectDomainSelectModelSchema.shape.dueDate.nullable(),
      description: ProjectDomainSelectModelSchema.shape.description.nullable(),
      thumbnailFileId:
        ProjectDomainSelectModelSchema.shape.thumbnailFileId.nullable(),
      biddingCloseDate:
        ProjectDomainSelectModelSchema.shape.biddingCloseDate.nullable(),
      bidCap: ProjectDomainSelectModelSchema.shape.bidCap.nullable(),
      bidRecommendation:
        ProjectDomainSelectModelSchema.shape.bidRecommendation.nullable(),
    })
    .partial()

export type ProjectDomainUpdateModel = ZodSimplify<
  typeof ProjectDomainUpdateModelSchema
>

/**
 * ProjectDomainSelectModelFilter
 */
export const ProjectDomainSelectModelFiltersSchema = z
  .object({
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectUrn: ModelFilterSchema(ProjectUrnSchema),
    packageId: ModelFilterSchema(PackageIdSchema),
    ownerId: ModelFilterSchema(UserIdSchema),
    brandTeamId: ModelFilterSchema(TeamIdSchema),
    acceptorId: ModelFilterSchema(UserIdSchema),
    agencyTeamId: ModelFilterSchema(TeamIdSchema),
    status: ModelFilterSchema(ProjectStatusSchema),
    search: NonEmptyStringSchema(),
    billingMethod: ModelFilterSchema(NoneableBillingMethodSchema),
    tags: TagFilterSchema,
    paymentCompletedAt: z.union([DateSchema, z.literal('undefined')]),
    origin: ModelFilterSchema(DataOriginSchema),
  })
  .partial()

export type ProjectDomainSelectModelFilters = ZodSimplify<
  typeof ProjectDomainSelectModelFiltersSchema
>
